import { Fragment } from "react";
import { Badge, Button, Icon, Typography } from "@/components/atoms";
import { useRemoveAccessMutation, useResendAccessMutation } from "@/redux/apis/assistants/assistantsApi";
import { AccessLink } from "@/redux/apis/assistants/types";
import { useAppSelector } from "@/redux/hooks";
import { configSelector } from "@/redux/slices/config/selectors";
import { addToast, clsx } from "@/utils";
import { formatLongDate } from "./helpers";

type StepState = "done" | "current" | "upcoming";
type Step = { label: string; state: StepState };

const Stepper = ({ steps }: { steps: Step[] }) => (
  <div className="mt-4 flex w-full max-w-xl items-center justify-center">
    {steps.map((step, index) => (
      <Fragment key={step.label}>
        <div className="flex shrink-0 items-center gap-2">
          {step.state === "upcoming" ? (
            <span className="h-3.5 w-3.5 shrink-0 rounded-full border-2 border-neutral-mid-gray" />
          ) : (
            <span className="h-3.5 w-3.5 shrink-0 rounded-full bg-success" />
          )}
          <Typography
            variant="action"
            className={clsx("whitespace-nowrap font-semibold", step.state === "upcoming" ? "text-neutral-silver-gray" : "text-success")}
          >
            {step.label}
          </Typography>
        </div>
        {index < steps.length - 1 && <span className="mx-3 h-px min-w-[24px] flex-1 bg-neutral-mid-gray" />}
      </Fragment>
    ))}
  </div>
);

interface PendingDetailPanelProps {
  link: AccessLink;
  /** Called after the invite/request is cancelled so the parent can clear the selection. */
  onCancelled: () => void;
  /** Operator's read-only EA view — hides the Cancel/Resend actions (the operator may only view the
   *  EA's pending requests, not act on them). Defaults to false (the EA's own view keeps the actions). */
  readOnly?: boolean;
  /**
   * Where this panel is rendered, which controls where the Cancel/Resend actions live:
   *  - "panel"  (default): desktop inline side panel — actions sit in the header (unchanged).
   *  - "modal"  : tablet centered Modal — actions move to a right-aligned footer row.
   *  - "drawer" : mobile bottom-sheet Drawer — actions move to a stacked, full-width footer.
   * The overlay variants append a "Close" button (needs `onClose`).
   */
  variant?: "panel" | "modal" | "drawer";
  /** Dismisses the overlay. Only used (and rendered as a "Close" button) in the modal/drawer variants. */
  onClose?: () => void;
}

/**
 * EA "My Clients" pending detail panel. Two variants keyed off `inviteState`:
 *  - INVITED (Figma 6271-17582) → brand-new email with no operator account yet. Rich panel:
 *    header (email + status pill + Cancel/Resend actions) over a card with the invite copy and a
 *    3-step progress stepper (Invitation Sent → Account Created → Account Access Approved).
 *  - AWAITING_APPROVAL (Figma 6269-18207) → existing account that must approve the request. Simple
 *    panel: header (email + amber status pill, no action buttons) over a timer card that just tells
 *    the EA we're waiting on the client. No stepper, no buttons.
 *
 * Actions (both variants): "Cancel Invite"/"Cancel Request" reuses the removeAccess (DELETE) mutation
 * on the pending link; "Resend Invite"/"Resend Request" re-notifies the party via the resendAccess
 * (POST .../resend) mutation. Both variants share the same Cancel + Resend header actions.
 */
export const PendingDetailPanel = ({ link, onCancelled, readOnly = false, variant = "panel", onClose }: PendingDetailPanelProps) => {
  const [removeAccess, { isLoading: isCancelling }] = useRemoveAccessMutation();
  const [resendAccess, { isLoading: isResending }] = useResendAccessMutation();
  const isInvited = link.inviteState === "INVITED";
  const email = link.party.email || link.party.name;
  const isOverlay = variant !== "panel";
  const cancelLabel = isInvited ? "Cancel Invite" : "Cancel Request";
  const resendLabel = isInvited ? "Resend Invite" : "Resend Request";
  // Operator/brand display name for the INVITED copy ("... a {operator} account"); trailing dots
  // stripped to match the "your operator" fallback used across the client portal.
  const operatorName = (useAppSelector(configSelector)?.name || "your operator").replace(/\.+$/, "");
  const sentOn = formatLongDate(link.createdAt);

  const handleCancel = async () => {
    try {
      await removeAccess(link.id).unwrap();
      addToast("success", isInvited ? `Invite to ${email} cancelled` : `Access request to ${email} cancelled`);
      onCancelled();
    } catch {
      addToast("danger", "Failed to cancel. Please try again.");
    }
  };

  const handleResend = async () => {
    try {
      await resendAccess(link.id).unwrap();
      addToast("success", "Request resent");
    } catch {
      addToast("danger", "Failed to resend. Please try again.");
    }
  };

  // Shared Cancel + Resend buttons — single source of truth so the desktop header and the
  // modal/drawer footer stay in lockstep. Suppressed entirely in the operator's read-only EA view.
  const cancelResendButtons = (stacked: boolean) =>
    readOnly ? null : (
      <>
        <Button
          variant="secondary"
          size={stacked ? "md" : "sm"}
          className={clsx("!border-danger !text-danger hover:!bg-danger-light", stacked && "w-full")}
          onClick={handleCancel}
          disabled={isCancelling}
        >
          {cancelLabel}
        </Button>
        <Button variant="secondary" size={stacked ? "md" : "sm"} className={clsx(stacked && "w-full")} onClick={handleResend} disabled={isResending}>
          {resendLabel}
        </Button>
      </>
    );

  // Header actions — desktop panel only (overlays move them to the footer).
  const headerActions = () =>
    !isOverlay && !readOnly ? <div className="flex shrink-0 flex-wrap gap-2">{cancelResendButtons(false)}</div> : null;

  // Overlay footer — modal: right-aligned row [Close · Cancel · Resend]; drawer: stacked, full-width.
  // readOnly collapses to just Close.
  const overlayFooter = () =>
    !isOverlay ? null : variant === "drawer" ? (
      <div className="flex flex-col gap-2 border-t border-neutral-gray pt-4">
        {cancelResendButtons(true)}
        <Button variant="tertiary" size="md" className="w-full" onClick={onClose}>
          Close
        </Button>
      </div>
    ) : (
      <div className="flex flex-wrap justify-end gap-2 border-t border-neutral-gray pt-4">
        <Button variant="tertiary" size="md" onClick={onClose}>
          Close
        </Button>
        {cancelResendButtons(false)}
      </div>
    );

  // AWAITING_APPROVAL — simple timer card, no stepper, but same Cancel/Resend header actions as INVITED.
  if (!isInvited) {
    return (
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <Typography variant="h3" className="truncate font-semibold text-neutral-black">
              {email}
            </Typography>
            <Badge
              type="warning"
              size="sm"
              message="Awaiting Approval"
              className="shrink-0 !rounded-full border border-warning !bg-transparent"
            />
          </div>
          {headerActions()}
        </div>

        <div className="rounded-lg border border-neutral-gray bg-neutral-surface-gray px-6 py-10">
          <div className="flex flex-col items-center text-center">
            <Icon name="Timer1" size="xl" className="text-neutral-dark-gray" />
            <Typography variant="title" className="mt-3 font-semibold text-neutral-black">
              Waiting for your client to approve
            </Typography>
            <Typography variant="paragraph" className="mt-1 max-w-lg text-neutral-dark-gray">
              We sent an access request to{" "}
              {/* Email in info blue per DSN-558 (node 6271-18150). */}
              <span className="text-info">{email}</span>
              {sentOn ? ` on ${sentOn}` : ""}. They can log in to approve it, or you can approve it if you manage their inbox. Once
              approved, you can manage their bookings and account details here.
            </Typography>
          </div>
        </div>

        {overlayFooter()}
      </div>
    );
  }

  // INVITED — rich panel with stepper + header actions.
  const steps: Step[] = [
    { label: "Invitation Sent", state: "current" },
    { label: "Account Created", state: "upcoming" },
    { label: "Account Access Approved", state: "upcoming" },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <Typography variant="h3" className="truncate font-semibold text-neutral-black">
            {email}
          </Typography>
          <Badge
            type="warning"
            size="sm"
            message="Invited to Join"
            className="shrink-0 !rounded-full border border-warning !bg-transparent"
          />
        </div>
        {headerActions()}
      </div>

      <div className="rounded-lg border border-neutral-gray bg-neutral-surface-gray px-6 py-10">
        <div className="flex flex-col items-center text-center">
          <Icon name="Sms" size="xl" className="text-neutral-dark-gray" />
          <Typography variant="title" className="mt-3 font-semibold text-neutral-black">
            Invitation sent
          </Typography>
          <Typography variant="paragraph" className="mt-1 max-w-lg text-neutral-dark-gray">
            {/* Email in info blue per DSN-558 (node 6271-18150). */}
            <span className="text-info">{email}</span> needs to create a {operatorName} account before they can approve your access. If
            you manage their inbox, you can complete both steps on their behalf.
          </Typography>
          <Stepper steps={steps} />
        </div>
      </div>

      {overlayFooter()}
    </div>
  );
};
